import { useCallback, useState } from 'react'

import { useGenerateImage } from 'hooks/queries/workspaces/useGenerateImage'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { useCreditUsage } from 'hooks/useCreditUsage'
import { useShowCreditsPaywall } from 'hooks/useShowCreditsPaywall'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useRootStore } from 'stores/RootStore'

import { ourUuid } from 'utils'
import { flatten } from 'lodash'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { reportError } from 'utils/error'

import { SUCCESS_RESPONSE_TYPE } from 'api/constants'

import { MediaFileClient } from 'api/clients/MediaFileClient'
import { GenerationSet, PromptValues } from '../GenerateImageDrawer'
import { GeneratedImage } from 'types'
import { AIRateLimitResponseError, AIInsufficientCreditsError } from 'api/clients/AiClient'
import { usePurchaseContentCreditsModal } from 'hooks/usePurchaseContentCreditsModal'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'

export type UseGenerateImagesResult = ReturnType<typeof useGenerateImages>

export const useGenerateImages = ({ containerRef }: { containerRef?: React.RefObject<HTMLDivElement> }) => {
  const openPurchaseContentCreditsModal = usePurchaseContentCreditsModal()
  const [previousValues, setPreviousValues] = useState<PromptValues>()
  const [generationSets, setGenerationSets] = useState<GenerationSet[]>([])
  const [requestId, setRequestId] = useState<number>()
  const [processingRequestIds, setProcessingRequestIds] = useState<number[]>([])
  const workspaceId = useCurrentWorkspaceId()
  const generateImageMutation = useGenerateImage({ workspaceId })
  const uploadsProjectKey = useUploadsProject(workspaceId).data?.project?.id
  const [viewingImage, setViewingImage] = useState<GeneratedImage & { requestId: number }>()
  const { usageCosts, creditsRemaining } = useCreditUsage()
  const { price, isTrackingCredits } = useShowCreditsPaywall()
  const { tierUsageStore } = useRootStore()
  const { currentSubscription } = tierUsageStore
  const isAiImageGenerationCreditsEnabled = useFlipperFlag('ai_image_generation_credits')

  const showInsufficientCreditsModal = (onSuccess?: () => void, imageCount = 2) => {
    const isPaid = currentSubscription?.status === 'active'
    const hasPriceForAddCredits = !!price
    if (isPaid && !hasPriceForAddCredits) return
    if (!price) return

    const cost = (usageCosts?.ai_image_credits_cost ?? 0) * imageCount
    openPurchaseContentCreditsModal(
      {
        price,
        onSuccess,
        defaultBatchCount: onSuccess ? Math.ceil(cost / 100) || 1 : 1,
      },
      { newStack: true },
    )
  }

  const handleMultiGenerate = async (values: PromptValues, promptDisplay?: string, numberOfImages = 2) => {
    const cost = (usageCosts?.ai_image_credits_cost ?? 0) * numberOfImages
    const isPaid = currentSubscription?.status === 'active'
    const isTrackingCreditsOrPioneer = isTrackingCredits || currentSubscription?.plan === PaymentPlanKey.VirtualMarketer
    const canShowPaywall = isAiImageGenerationCreditsEnabled && isTrackingCreditsOrPioneer && creditsRemaining < cost
    const hasPriceOrUpgradePath = (isPaid && !!price) || !isPaid

    if (canShowPaywall && hasPriceOrUpgradePath) {
      showInsufficientCreditsModal(() => handleMultiGenerate(values, promptDisplay, numberOfImages), numberOfImages)
      return
    }

    setPreviousValues(values)

    const prompt = {
      topic: values.topic,
      image_type: values.image_type,
      image_aspect_ratio: values.image_aspect_ratio,
      priority: values.priority,
    }

    const uuid = ourUuid()
    const model = values.priority === 'speed' ? 'mystic' : 'openai'

    const promises = new Array(numberOfImages).fill(null).map(async () => {
      return generateImageMutation
        .mutateAsync({
          model,
          prompt,
        })
        .then((res) => {
          setProcessingRequestIds((prev) => [...prev, res.request.id])
          setGenerationSets((prev) => {
            const request = {
              id: res.request.id,
              prompt,
              isSaved: false,
              isFailed: res.request.status === 'failed',
              generatedContent: null,
            }

            if (!prev.map((set) => set.uuid).includes(uuid)) {
              return [
                ...prev,
                {
                  uuid,
                  requests: [request],
                  promptDisplay: promptDisplay || prompt.topic,
                },
              ]
            }

            return prev.map((set) => {
              if (set.uuid === uuid) {
                return {
                  ...set,
                  requests: [...set.requests, request],
                }
              }

              return set
            })
          })
        })
        .catch((error) => {
          if (error instanceof AIRateLimitResponseError) {
            return 'rateLimited'
          }
          if (error instanceof AIInsufficientCreditsError) {
            return 'insufficientCredits'
          }
          reportError(error)
        })
    })

    const responses = await Promise.all(promises)

    if (responses.find((res) => res === 'rateLimited')) {
      showToastMessage(
        'You have exceeded the maximum daily image generations allowed. Please try again tomorrow.',
        'error',
      )
    }

    if (isAiImageGenerationCreditsEnabled && responses.find((res) => res === 'insufficientCredits')) {
      showInsufficientCreditsModal(() => handleMultiGenerate(values, promptDisplay, numberOfImages), numberOfImages)
    }

    setTimeout(() => {
      if (containerRef?.current) {
        containerRef.current.scrollTo({
          top: containerRef.current.scrollHeight,
          behavior: 'smooth',
        })
      }
    }, 500)
  }

  const handleGenerate = async (values: PromptValues) => {
    try {
      setPreviousValues(values)
      const response = await generateImageMutation.mutateAsync({
        model: 'openai',
        prompt: {
          topic: values.topic,
          image_type: values.image_type,
          image_aspect_ratio: values.image_aspect_ratio,
          priority: values.priority,
        },
      })
      setRequestId(response.request.id)
    } catch (error) {
      reportError(error)
    }
  }

  const onRetry = (requestId: number) => {
    const allRequests = flatten(generationSets.map((set) => set.requests))
    const optionIndex = allRequests.findIndex((req) => req.id === requestId)
    const prompt = allRequests[optionIndex].prompt
    const model = prompt.priority === 'speed' ? 'mystic' : 'openai'

    return generateImageMutation
      .mutateAsync({
        model,
        prompt,
      })
      .then((res) => {
        setGenerationSets((prev) => {
          const request = {
            id: res.request.id,
            prompt,
            isSaved: false,
            isFailed: res.request.status === 'failed',
            generatedContent: null,
          }

          return prev.map((set) => {
            const requests = set.requests.map((req) => {
              if (req.id === requestId) {
                return request
              }

              return req
            })

            return {
              ...set,
              requests,
            }
          })
        })
      })
      .catch((error) => {
        reportError(error)
      })
  }

  const onSaveImage = async (img: GeneratedImage, requestId: number) => {
    if (!uploadsProjectKey) {
      showToastMessage('Unable to find uploads folder', 'error')
      reportError('Unable to find uploads folder')
      return
    }
    const request = flatten(generationSets.map((set) => set.requests)).find((request) => request.id === requestId)

    if (request?.isSaved) {
      showToastMessage('Image is already saved to your uploads!')
      return
    }

    const dismissUploading = showToastMessage('Uploading...', 'loading', undefined, 0)

    try {
      const res = await MediaFileClient.create(workspaceId, {
        source_url: img.url,
        mime_type: img.mimeType,
        title: 'AI Generated Image',
        project_key: uploadsProjectKey,
      })
      if (res.type === SUCCESS_RESPONSE_TYPE) {
        const newGenerationSets = generationSets.map((generationSet) => {
          if (generationSet.requests.map((r) => r.id).includes(requestId)) {
            return {
              ...generationSet,
              requests: generationSet.requests.map((request) => {
                if (request.id === requestId) {
                  return {
                    ...request,
                    isSaved: true,
                  }
                }

                return request
              }),
            }
          }

          return generationSet
        })
        setGenerationSets(newGenerationSets)
        showToastMessage('Image is saved to your uploads!')
        dismissUploading()
      }
    } catch (error) {
      dismissUploading()
      showToastMessage('We were unable to save your image', 'error')
      reportError(error)
    }
  }

  const onDeleteImage = (requestId: number) => {
    const newGenerationSets = generationSets.map((generationSet) => {
      if (generationSet.requests.map((r) => r.id).includes(requestId)) {
        return {
          ...generationSet,
          requests: generationSet.requests.filter((request) => request.id !== requestId),
        }
      }

      return generationSet
    })
    setGenerationSets(newGenerationSets)
  }

  const onGenerateMoreLikeThis = (requestId: number) => {
    const allRequests = flatten(generationSets.map((set) => set.requests))
    const optionIndex = allRequests.findIndex((req) => req.id === requestId)
    const promptDisplay = `Generate more like option ${optionIndex + 1}.`
    const values = allRequests[optionIndex].prompt

    handleMultiGenerate(values, promptDisplay)
  }

  const onCancelGeneration = () => {
    setProcessingRequestIds([])
  }

  const removeProcessingId = useCallback((id: number) => {
    setProcessingRequestIds((prev) => prev.filter((reqId) => reqId !== id))
  }, [])

  const isProcessing = processingRequestIds.length > 0

  const insufficientCreditsCtaLabel = currentSubscription?.status === 'active' ? 'Add Credits' : 'Upgrade'

  return {
    handleMultiGenerate,
    handleGenerate,
    generationSets,
    setGenerationSets,
    onRetry,
    previousValues,
    requestId,
    setRequestId,
    onSaveImage,
    onDeleteImage,
    onGenerateMoreLikeThis,
    onCancelGeneration,
    viewingImage,
    setViewingImage,
    isProcessing,
    processingRequestIds,
    removeProcessingId,
    showInsufficientCreditsModal,
    insufficientCreditsCtaLabel,
  }
}
